import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ChatHistory, ChatHistoryItem } from '../store/chat-history/types';
import { startOfDay, startOfYear, subDays, isBefore, parseISO } from 'date-fns';
import Bugsnag from '@bugsnag/js';
import { jwtDecode } from 'jwt-decode';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isBrowser() {
  return typeof window !== 'undefined';
}

export function toQueryString(params: QueryParams): string {
  const queries: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      queries.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return queries.length > 0 ? `?${queries.join('&')}` : '';
}

export const getLocalDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const groupChatHistory = (
  chatItems?: ChatHistoryItem[],
): ChatHistory[] => {
  if (!chatItems?.length) return [];

  const now = new Date();
  const todayStart = startOfDay(now);
  const previous7DaysStart = subDays(todayStart, 7);
  const previous30DaysStart = subDays(todayStart, 30);
  const yearStart = startOfYear(now);

  const resolveItemDate = (item: ChatHistoryItem): Date | null => {
    try {
      if (item.updatedAt) {
        const d = parseISO(item.updatedAt);
        if (!isNaN(d.getTime())) return d;
      }
      if (item.createdAt) {
        const num = item.createdAt;
        const ms = num < 1e12 ? num * 1000 : num;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d;
      }
    } catch (_) {
      return null;
    }
    return null;
  };

  const groups: ChatHistory[] = [
    { title: 'Today', items: [] },
    { title: 'Previous 7 Days', items: [] },
    { title: 'Previous 30 Days', items: [] },
    { title: 'Previous Months', items: [] },
    { title: 'Previous Years', items: [] },
  ];

  const isAfterOrEqual = (date: Date, boundary: Date) =>
    !isBefore(date, boundary);

  chatItems.forEach((item) => {
    const date = resolveItemDate(item);
    if (!date) return; // skip invalid

    if (isAfterOrEqual(date, todayStart)) {
      groups[0].items.push(item);
    } else if (isAfterOrEqual(date, previous7DaysStart)) {
      groups[1].items.push(item);
    } else if (isAfterOrEqual(date, previous30DaysStart)) {
      groups[2].items.push(item);
    } else if (isAfterOrEqual(date, yearStart)) {
      groups[3].items.push(item);
    } else {
      groups[4].items.push(item);
    }
  });

  groups.forEach((group) => {
    group.items.sort((a, b) => {
      const da = resolveItemDate(a)?.getTime() || 0;
      const db = resolveItemDate(b)?.getTime() || 0;
      return db - da;
    });
  });

  return groups.filter((g) => g.items.length > 0);
};

export function isRouteActive(target: string, current: string, depth = 0) {
  if (current === target) return true;

  if (current.startsWith(target)) {
    const boundary = current.charAt(target.length);
    if (boundary === '/') {
      if (depth <= 0) return true;

      const tSeg = target.split('/').filter(Boolean).length;
      const cSeg = current.split('/').filter(Boolean).length;
      return cSeg - tSeg <= depth;
    }
  }

  return false;
}

function getErrorMessage(error: Error | string | unknown) {
  return error instanceof Error ? error.message : String(error);
}

export type CreateURLFields = Record<string, string>;

export function createURL(urlString: string, fields: CreateURLFields) {
  try {
    const url = new URL(urlString);
    const params = new URLSearchParams();

    Object.entries(fields || {}).forEach(([key, value]) => {
      params.append(key, String(value));
    });

    url.search = params.toString();

    return url;
  } catch (error) {
    Bugsnag.notify(`Error creating URL: ${getErrorMessage(error as unknown)}`);
    return '';
  }
}

type CognitoJwtPayload = {
  exp?: number;
  iss?: string;
  aud?: string | string[];
  [key: string]: unknown;
};

const normalizeIssuer = (issuer?: string) => (issuer || '').replace(/\/+$/, '');

export function isValidRgCognitoToken(token: string) {
  try {
    const payload = jwtDecode<CognitoJwtPayload>(token);

    if (!payload?.['custom:rg_manager_user_id'] && !payload?.['custom:op_userId']) return false;

    const nowSeconds = Math.floor(Date.now() / 1000);
    if (payload?.exp && payload.exp <= nowSeconds) {
      return false;
    }

    const expectedIssuer = process.env.NEXT_PUBLIC_COGNITO_AUTHORITY;
    if (expectedIssuer) {
      if (
        !payload.iss ||
        normalizeIssuer(payload.iss) !== normalizeIssuer(expectedIssuer)
      ) {
        return false;
      }
    }

    const expectedAudience = process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID;
    if (expectedAudience) {
      const aud = payload.aud;
      const audValid =
        typeof aud === 'string'
          ? aud === expectedAudience
          : Array.isArray(aud)
            ? aud.includes(expectedAudience)
            : false;
      if (!audValid) return false;
    }

    return true;
  } catch {
    return false;
  }
}
